import axios from 'axios';
import { API_BASE_URL } from '../config';
import { getToken } from '../utils/auth';

// Types for employee data
export interface Employee {
  id: number;
  name: string;
  dateOfBirth: string;
  mobileNumber: string;
  rateType: 'daily' | 'monthly';
  rateAmount: number;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeShift {
  id: number;
  employeeId: number;
  date: string;
  startTime?: string;
  endTime?: string;
  status: 'scheduled' | 'present' | 'absent' | 'late';
  lateMinutes?: number;
  isRecurring: boolean;
  recurringPattern?: 'weekly' | 'monthly';
  notes?: string;
  employee?: Employee;
}

// Get all employees
export const getEmployees = async (): Promise<Employee[]> => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_BASE_URL}/employees`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching employees:', error);
    throw error;
  }
};

// Get a single employee by ID
export const getEmployee = async (id: number): Promise<Employee> => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_BASE_URL}/employees/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching employee with id ${id}:`, error);
    throw error;
  }
};

// Create a new employee
export const createEmployee = async (employeeData: Partial<Employee>): Promise<Employee> => {
  try {
    const token = getToken();
    const response = await axios.post(`${API_BASE_URL}/employees`, employeeData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating employee:', error);
    throw error;
  }
};

// Update an employee
export const updateEmployee = async (id: number, employeeData: Partial<Employee>): Promise<void> => {
  try {
    const token = getToken();
    await axios.put(`${API_BASE_URL}/employees/${id}`, employeeData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  } catch (error) {
    console.error(`Error updating employee with id ${id}:`, error);
    throw error;
  }
};

// Delete an employee
export const deleteEmployee = async (id: number): Promise<void> => {
  try {
    const token = getToken();
    await axios.delete(`${API_BASE_URL}/employees/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error(`Error deleting employee with id ${id}:`, error);
    throw error;
  }
};

// Get employee shifts
export const getEmployeeShifts = async (employeeId: number, startDate?: string, endDate?: string): Promise<EmployeeShift[]> => {
  try {
    const token = getToken();
    let url = `${API_BASE_URL}/employees/${employeeId}/shifts`;
    
    // Add date range filters if provided
    if (startDate || endDate) {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      url += `?${params.toString()}`;
    }
    
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching shifts for employee ${employeeId}:`, error);
    throw error;
  }
};

// Create a shift for an employee
export const createEmployeeShift = async (employeeId: number, shiftData: Partial<EmployeeShift>): Promise<EmployeeShift> => {
  try {
    const token = getToken();
    const response = await axios.post(`${API_BASE_URL}/employees/${employeeId}/shifts`, shiftData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error creating shift for employee ${employeeId}:`, error);
    throw error;
  }
};

// Get upcoming shifts (today and tomorrow)
export const getUpcomingShifts = async (): Promise<{ today: EmployeeShift[], tomorrow: EmployeeShift[] }> => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_BASE_URL}/employees/upcoming-shifts`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching upcoming shifts:', error);
    throw error;
  }
};

// Update a shift for an employee
export const updateEmployeeShift = async (employeeId: number, shiftId: number, shiftData: Partial<EmployeeShift>): Promise<void> => {
  try {
    const token = getToken();
    await axios.put(`${API_BASE_URL}/employees/${employeeId}/shifts/${shiftId}`, shiftData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  } catch (error) {
    console.error(`Error updating shift ${shiftId} for employee ${employeeId}:`, error);
    throw error;
  }
};

// Delete a shift for an employee
export const deleteEmployeeShift = async (employeeId: number, shiftId: number): Promise<void> => {
  try {
    const token = getToken();
    await axios.delete(`${API_BASE_URL}/employees/${employeeId}/shifts/${shiftId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error(`Error deleting shift ${shiftId} for employee ${employeeId}:`, error);
    throw error;
  }
}; 